import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../Services/axiosInstance';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { User, Database, RefreshCw, CheckSquare } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { taskService } from '../Services/taskService';
import type { TaskItemDto } from '../types/tasks';

interface ProfileResponse {
  name?: string;
  email?: string;
  role?: string;
}

interface StatisticsResponse {
  count: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [usersCount, setUsersCount] = useState<number | null>(null);
  const [subscriptionsCount, setSubscriptionsCount] = useState<number | null>(null);
  const [usersCountLoading, setUsersCountLoading] = useState(true);
  const [subscriptionsCountLoading, setSubscriptionsCountLoading] = useState(true);
  const [usersCountError, setUsersCountError] = useState(false);
  const [subscriptionsCountError, setSubscriptionsCountError] = useState(false);
  const [myTasks, setMyTasks] = useState<TaskItemDto[]>([]);
  const [myTasksLoading, setMyTasksLoading] = useState(true);
  const [myTasksError, setMyTasksError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const fetchDashboardData = async () => {
      const profilePromise = axiosInstance.get('/api/Profile', { signal: controller.signal })
        .then(res => {
          setProfile(res.data);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error('Error fetching profile:', error);
          setProfile(null);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });

      const usersPromise = axiosInstance.get<StatisticsResponse>('/api/Statistics/users', { signal: controller.signal })
        .then(res => {
          setUsersCount(res.data.count);
          setUsersCountError(false);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error('Error fetching users count:', error);
          setUsersCountError(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setUsersCountLoading(false);
        });

      const subscriptionsPromise = axiosInstance.get<StatisticsResponse>('/api/Statistics/subscriptions', { signal: controller.signal })
        .then(res => {
          setSubscriptionsCount(res.data.count);
          setSubscriptionsCountError(false);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error('Error fetching subscriptions count:', error);
          setSubscriptionsCountError(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setSubscriptionsCountLoading(false);
        });

      const myTasksPromise = taskService.getMyTasks()
        .then(data => {
          setMyTasks(data);
          setMyTasksError(false);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error('Error fetching my tasks:', error);
          setMyTasksError(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setMyTasksLoading(false);
        });

      await Promise.all([profilePromise, usersPromise, subscriptionsPromise, myTasksPromise]);
    };

    fetchDashboardData();

    return () => controller.abort();
  }, []);

  if (loading) return <LoadingSpinner label="Loading dashboard..." />;

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6">
        {/* Welcome Banner */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 md:p-8">
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-primary/5 blur-2xl" />
          <div className="relative">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              {greeting()}{profile?.name ? `, ${profile.name}` : ''}
            </h1>
            <p className="text-muted-foreground mt-1.5 text-sm md:text-base">
              Here's an overview of your application, users, and subscriptions.
            </p>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 stagger-children">
          {/* Profile Card */}
          <Card className="group relative overflow-hidden animate-fade-in-up">
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/80 to-primary/20" />
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary transition-transform duration-200 group-hover:scale-110">
                  <User size={18} />
                </div>
                <span>Profile</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile ? (
                <div className="space-y-1">
                  <div className="text-2xl font-bold">{profile.name}</div>
                  <div className="text-sm text-muted-foreground">{profile.email}</div>
                  <div className="text-sm text-muted-foreground">{profile.role}</div>
                </div>
              ) : (
                <div className="text-muted-foreground">No profile data</div>
              )}
            </CardContent>
          </Card>

          {/* Users Card */}
          <Card
            className="group relative overflow-hidden cursor-pointer animate-fade-in-up transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
            onClick={() => navigate('/users')}
          >
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500/80 to-blue-500/20" />
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500 transition-transform duration-200 group-hover:scale-110">
                  <User size={18} />
                </div>
                <span>Users</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {usersCountLoading ? (
                <Skeleton className="h-10 w-16 mb-2" />
              ) : usersCountError ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-3xl font-bold cursor-help">—</div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Unable to load user count</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="text-3xl font-bold">{usersCount ?? 0}</div>
              )}
              <div className="text-sm text-muted-foreground">Total users</div>
            </CardContent>
          </Card>

          {/* Subscriptions Card */}
          <Card className="group relative overflow-hidden animate-fade-in-up transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500/80 to-emerald-500/20" />
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500 transition-transform duration-200 group-hover:scale-110">
                  <Database size={18} />
                </div>
                <span>Subscriptions</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscriptionsCountLoading ? (
                <Skeleton className="h-10 w-16 mb-2" />
              ) : subscriptionsCountError ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-3xl font-bold cursor-help">—</div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Unable to load subscriptions count</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="text-3xl font-bold">{subscriptionsCount ?? 0}</div>
              )}
              <div className="text-sm text-muted-foreground">Active subscriptions</div>
            </CardContent>
          </Card>

          {/* My Tasks Card */}
          <Card
            className="group relative overflow-hidden cursor-pointer animate-fade-in-up transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
            onClick={() => navigate('/my-tasks')}
          >
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500/80 to-violet-500/20" />
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/10 text-violet-500 transition-transform duration-200 group-hover:scale-110">
                  <CheckSquare size={18} />
                </div>
                <span>My Tasks</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myTasksLoading ? (
                <Skeleton className="h-10 w-16 mb-2" />
              ) : myTasksError ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-3xl font-bold cursor-help">—</div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Unable to load task count</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="text-3xl font-bold">{myTasks.length}</div>
              )}
              <div className="text-sm text-muted-foreground">Assigned to you</div>
              {!myTasksLoading && !myTasksError && myTasks.length > 0 && (
                <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                  <span>{myTasks.filter(t => t.status === 'Todo').length} to do</span>
                  <span>{myTasks.filter(t => t.status === 'InProgress').length} in progress</span>
                  <span>{myTasks.filter(t => t.status === 'Done').length} done</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="animate-fade-in-up">
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-muted/30 py-12">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="rounded-full bg-muted/80 p-3 ring-1 ring-border/50">
                  <RefreshCw size={24} className="text-muted-foreground" />
                </div>
                <p className="font-medium text-muted-foreground">No recent activity</p>
                <p className="text-sm text-muted-foreground/75">Activity will appear here when you start using the app</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default Dashboard;
